import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const CreateRadioStep1 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    description: '',
    genre: '',
    language: '',
  });
  const [plan, setPlan] = useState(null);
  const [checking, setChecking] = useState(true);

  // Vérifie le quota avant de laisser l'utilisateur entrer dans le tunnel de création.
  useEffect(() => {
    let mounted = true;
    api.get('/api/me/plan')
      .then((r) => { if (mounted) setPlan(r.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setChecking(false); });
    return () => { mounted = false; };
  }, []);

  const atLimit =
    plan && (plan.usage?.radios ?? 0) >= (plan.quota?.maxRadios ?? 0);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleNext = () => {
    if (!form.name || !form.genre || !form.language) {
      alert(t('createRadio.fillRequired'));
      return;
    }

    localStorage.setItem('radioDraft', JSON.stringify(form));
    navigate('/creer-radio/step2');
  };

  return (
    <div className="bg-gray-50 min-h-screen pt-28 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Titre et étapes */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('createRadio.title')}</h1>
          <p className="text-gray-600 mt-1">{t('createRadio.subtitle')}</p>
          <div className="flex justify-center gap-4 mt-6">
            <span className="w-8 h-8 flex items-center justify-center bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full font-semibold">1</span>
            <div className="w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-600 rounded-full">2</div>
            <div className="w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-600 rounded-full">3</div>
          </div>
          <div className="flex justify-center gap-8 text-sm mt-2 text-gray-500">
            <span className="text-purple-600 font-medium">{t('createRadio.stepInfo')}</span>
            <span>{t('createRadio.stepCustom')}</span>
            <span>{t('createRadio.stepConfig')}</span>
          </div>
        </div>

        {atLimit ? (
          /* Quota atteint : on bloque avant l'appel serveur (403) */
          <div className="bg-white shadow-md rounded-lg p-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center text-2xl mx-auto mb-4">⚠</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('createRadio.limitReached')}</h2>
            <p className="text-gray-600 mb-1">
              {t('createRadio.planAllows', { plan: plan.plan, count: plan.quota?.maxRadios })}
            </p>
            <p className="text-gray-600 mb-6">
              {t('createRadio.alreadyHave', { count: plan.usage?.radios })}
            </p>
            <div className="flex justify-center gap-3">
              <button onClick={() => navigate('/mes-radios')} className="bg-gray-100 text-gray-700 px-6 py-2 rounded hover:bg-gray-200">
                {t('nav.myRadios')}
              </button>
              <a href="/#pricing" className="bg-gradient-to-r from-purple-600 to-blue-500 text-white px-6 py-2 rounded hover:opacity-90">
                {t('createRadio.seePlans')}
              </a>
            </div>
          </div>
        ) : (
          /* Formulaire */
          <div className="bg-white shadow-md rounded-lg p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('createRadio.baseInfo')}</h2>

            <div className="mb-4">
              <label className="block font-medium text-gray-700 mb-1">{t('createRadio.nameLabel')}</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={t('createRadio.namePlaceholder')}
                className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="mb-4">
              <label className="block font-medium text-gray-700 mb-1">{t('createRadio.descLabel')}</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder={t('createRadio.descPlaceholder')}
                className="w-full border border-gray-300 rounded px-4 py-2 min-h-[100px] focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="flex gap-4 mb-6">
              <div className="w-1/2">
                <label className="block font-medium text-gray-700 mb-1">{t('createRadio.genreLabel')}</label>
                <select
                  name="genre"
                  value={form.genre}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">{t('createRadio.genrePlaceholder')}</option>
                  <option value="Pop">{t('createRadio.genre.pop')}</option>
                  <option value="Rock">{t('createRadio.genre.rock')}</option>
                  <option value="Jazz">{t('createRadio.genre.jazz')}</option>
                  <option value="Électro">{t('createRadio.genre.electro')}</option>
                  <option value="Classique">{t('createRadio.genre.classical')}</option>
                </select>
              </div>
              <div className="w-1/2">
                <label className="block font-medium text-gray-700 mb-1">{t('createRadio.langLabel')}</label>
                <select
                  name="language"
                  value={form.language}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">{t('createRadio.langPlaceholder')}</option>
                  <option value="Français">{t('createRadio.language.french')}</option>
                  <option value="Arabe">{t('createRadio.language.arabic')}</option>
                  <option value="Anglais">{t('createRadio.language.english')}</option>
                </select>
              </div>
            </div>

            {checking && <p className="text-xs text-gray-400 mb-4">{t('createRadio.checkingPlan')}</p>}

            {/* Boutons */}
            <div className="flex justify-between mt-8">
              <button
                className="bg-gray-100 text-gray-700 px-6 py-2 rounded hover:bg-gray-200"
                onClick={() => navigate('/')}
              >
                {t('createRadio.cancel')}
              </button>
              <button
                onClick={handleNext}
                className="bg-gradient-to-r from-purple-600 to-blue-500 text-white px-6 py-2 rounded hover:opacity-90"
              >
                {t('createRadio.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateRadioStep1;
